/**
 * User-perceived order latency: final click -> matching holding render opportunity.
 * Monotonic same-page timings only. Never invent a render after reload/unmount.
 */
export type ExperienceReport = {
  attemptId: string; requestId: string; orderId: string | null;
  outcome: 'RENDERED' | 'FAILED' | 'UNKNOWN' | 'NOT_RENDERED';
  page: 'TRADE' | 'AI' | 'MODAL'; totalMs: number;
  prepareMs: number | null; requestMs: number | null; renderMs: number | null;
  backgrounded: boolean;
};
type Attempt = { id: string; wallet: string; page: ExperienceReport['page']; start: number;
  sent?: number; received?: number; requestId?: string; orderId?: string; clientOrderId?: string;
  timings?: OrderRequestTiming[];
  backgrounded: boolean; timer?: ReturnType<typeof setTimeout>; stop?: () => void };
export type OrderRequestTiming = {
  phase: 'preview' | 'ticket' | 'prepare' | 'submit' | 'resolve' | 'detail';
  durationMs: number; status: number; businessMs?: number;
};
let lastTiming: { attemptId: string; timings: OrderRequestTiming[] } | null = null;
/** Local diagnostics only: keep the existing telemetry API schema unchanged. */
export const getLastOrderTimings = () => lastTiming;
let current: Attempt | null = null;
let reporter: (r: ExperienceReport, wallet: string) => void = () => {};
let walletAddress: () => string = () => '';
const wallet = () => walletAddress().toLowerCase().trim();
const cards = new Map<HTMLElement, string>();
const now = () => performance.now();
const id = () => {
  try { return globalThis.crypto.randomUUID(); } catch { return ''; }
};
export function setOrderExperienceReporter(report: typeof reporter, address: () => string) {
  reporter=report;walletAddress=address;
}
function active() {
  if(current && current.wallet!==wallet()) {
    clearTimeout(current.timer);current.stop?.();current=null;
  }
  return current;
}

export function beginOrderExperience(page: Attempt['page']): string {
  // A repeat click/recovery must not reset T0 or close the original measurement.
  if(active())return '';
  if(!wallet())return '';
  const attemptID=id();
  if(!attemptID)return ''; // Unsupported browser must not prevent an order.
  const attempt: Attempt={id:attemptID,wallet:wallet(),page,start:now(),backgrounded:typeof document!=='undefined' && document.hidden};
  current=attempt;
  if(typeof document!=='undefined') {
    const onVisibility=()=>{
      if(document.hidden)attempt.backgrounded=true;
      else for(const [el,value] of cards)rendered(el,{value});
    };
    document.addEventListener('visibilitychange',onVisibility);
    attempt.stop=()=>document.removeEventListener('visibilitychange',onVisibility);
  }
  // Bound memory. Unknown/recovery stays timed from the original click for two minutes.
  attempt.timer=setTimeout(()=>finishOrderExperience(attempt.id,attempt.orderId?'NOT_RENDERED':'UNKNOWN'),120000);
  return attempt.id;
}

export function finishOrderExperience(attemptId: string, outcome: ExperienceReport['outcome']) {
  const a=active();
  if(!a || a.id!==attemptId)return;
  current=null;
  clearTimeout(a.timer);a.stop?.();
  const end=now();
  lastTiming = { attemptId: a.id, timings: [...(a.timings || [])] };
  try { console.debug('[OrderTiming]', lastTiming); } catch { /* diagnostics only */ }
  try { reporter({attemptId:a.id,requestId:a.requestId??'',orderId:a.orderId??null,outcome,page:a.page,
    totalMs:end-a.start,prepareMs:a.sent===undefined?null:a.sent-a.start,
    requestMs:a.sent===undefined||a.received===undefined?null:a.received-a.sent,
    renderMs:a.received===undefined||outcome!=='RENDERED'?null:end-a.received,backgrounded:a.backgrounded},a.wallet); } catch {/* Never affect orders. */}
}

/** Separates request legs; background prefetch is never charged to a click. */
export function trackOrderRequestTiming(path: string, method: string, body: unknown, prefetch = false) {
  const a = active();
  let phase: OrderRequestTiming['phase'] | undefined;
  if (method === 'POST' && path === '/v1/orders/preview') phase = 'preview';
  else if (method === 'POST' && path === '/v1/orders') phase = 'submit';
  else if (method === 'GET' && /^\/v1\/orders\/[^/]+$/.test(path)) phase = 'detail';
  else if (method === 'POST' && path === '/v1/orders/submission') {
    const action = (body as { action?: string })?.action;
    if (action === 'ticket' || action === 'prepare' || action === 'resolve') phase = action;
  }
  const start = now();
  let finished = false;
  return (status: number, headers?: Record<string, unknown>) => {
    if (!a || !phase || prefetch || finished || active() !== a) return;
    finished = true;
    const timing: OrderRequestTiming = { phase, durationMs: now() - start, status };
    const key = Object.keys(headers || {}).find(name => name.toLowerCase() === 'server-timing');
    const business = key ? /(?:^|,)\s*order_business\s*;\s*dur=([\d.]+)/i.exec(String(headers?.[key])) : null;
    if (business && Number.isFinite(Number(business[1]))) timing.businessMs = Number(business[1]);
    (a.timings ??= []).push(timing);
  };
}

export function orderExperienceError(attemptId: string, error: unknown) {
  const code=String((error as {code?:string})?.code??'');
  // Conversion retries and uncertain submissions retain the original T0.
  if(['ORDER_CONFIRMING','INSURANCE_CONVERSION_REQUIRED'].includes(code))return;
  finishOrderExperience(attemptId,'FAILED');
}

export function endOrderClick(attemptId: string) {
  if(current?.id===attemptId && current.sent===undefined)finishOrderExperience(attemptId,'FAILED');
}

export function orderRequestStarted(path: string, method: string, body: unknown, clientOrderId?: string): string | undefined {
  const prepare=path==='/v1/orders/submission' && (body as {action?:string})?.action==='prepare';
  if(!active() || !current || method!=='POST' || (path!=='/v1/orders' && !prepare))return;
  if(prepare)clientOrderId=(body as {clientOrderId?:string})?.clientOrderId;
  if(current.clientOrderId && current.clientOrderId!==clientOrderId)return;
  current.sent??=now();
  current.clientOrderId=clientOrderId;
  current.requestId=id();
  return current.requestId;
}

export function orderResponseReceived(path: string, method: string, body: unknown, status: number, requestId?: string, requestWallet?: string) {
  if(!active() || !current || requestWallet?.toLowerCase().trim()!==current.wallet ||
    status<200 || status>=300 || !body || typeof body!=='object')return;
  const order=body as {id?:string;orderId?:string;clientOrderId?:string};
  const orderID=order.orderId||order.id;
  if(!orderID)return;
  if(!(path==='/v1/orders'&&method==='POST'&&requestId===current.requestId) &&
    !(method==='GET'&&path.startsWith('/v1/orders/')&&!!current.clientOrderId&&order.clientOrderId===current.clientOrderId))return;
  current.orderId=orderID;
  current.received??=now();
}

// Vue directive on the actual current-holding card, not an optimistic spinner.
function rendered(el: HTMLElement,binding: {value: string}) {
  cards.set(el,binding.value);
  const a=active();
  if(!a?.orderId || binding.value!==a.orderId || typeof requestAnimationFrame==='undefined')return;
  requestAnimationFrame(()=>requestAnimationFrame(()=>{
    if(active()!==a || cards.get(el)!==a.orderId || !el.isConnected || document.hidden || !el.getClientRects().length)return;
    finishOrderExperience(a.id,'RENDERED');
  }));
}
export const vOrderRendered={mounted:rendered,updated:rendered,unmounted:(el:HTMLElement)=>cards.delete(el)};
